import { DocumentData } from "@king-ict/eupisi-ui/models";

import { normalizeEnsambleValue } from "../../utils/isNonEmptyEnsamble";
import { AdditionalDocumentation } from "./AdditionalDocumentation";
import { Competence, createCompetenceInitial } from "./Competence";
import { createResolutionDocument, ExecutionResolutionDocument } from "./ExecutionResolutionDocument";
import { Module } from "./Module";
import { OtherLegalEntityLocation } from "./OtherLegalEntityLocation";
import { ProgramRequest } from "./ProgramRequest";
import { ProgramRequestDetail, ProgramRequestDetailBase } from "./ProgramRequestDetail";
// import { OfficialDocumentType } from "./OfficialDocumentType";
import { ProgramRequestStatus } from "./ProgramRequestStatus";
import { ProgramRequestTeacher } from "./ProgramRequestTeacher"
import { ResolutionDocumentType } from "./ResolutionDocumentType";

export interface ProgramRequestData extends ProgramRequestDetailBase {
  detailId: number;
  institutionId: number;
  requestDate?: Date;
  programTypeId: number;
  statusId?: ProgramRequestStatus;
  remarkInstitution?: string;
  remark?: string;
  otherEnrollmentConditions: string[];
  otherFormsOfTeaching: string[];
  otherKnowledgeChecks: string[];
  executionResolutionDocuments: ExecutionResolutionDocument[];
  curriculumProgramDocument?: DocumentData;
  locationInsideHeadquarters: boolean;
  locationOutsideHeadquarters: boolean;
  locationOtherLegalEntity: boolean;
  locationOutsideHeadquartersIds: number[];
  otherLegalEntityLocations: OtherLegalEntityLocation[];
  competences: Competence[];
  modules: Module[];
  teachers: ProgramRequestTeacher[];  
  additionalDocumentation: AdditionalDocumentation[];
}

export function createProgramRequestData(): ProgramRequestData {
  return {
    id: 0,
    detailId: 0,
    stateId: 1,
    institutionId: 0,
    programTypeId: 0,
    programName: "",
    guidedLearningDuration: 0,
    independentLearningDuration: 0,
    workLearningDuration: 0,
    hkoLevelId: 0,
    languageLevelGeneralIds: [] as number[],
    languageLevelBusinessIds: [] as number[],
    formOfTeachingIds: [] as number[],
    knowledgeChecksIds: [] as number[],
    enrollmentConditionsIds: [],
    otherEnrollmentConditions: [],
    otherFormsOfTeaching: [],
    otherKnowledgeChecks: [],
    executionResolutionDocuments: [createResolutionDocument(ResolutionDocumentType.ExecutionResolution, "")],
    locationInsideHeadquarters: false,
    locationOutsideHeadquarters: false,
    locationOtherLegalEntity: false,
    locationOutsideHeadquartersIds: [] as number[],
    otherLegalEntityLocations: [],
    // competences: [] as Competence[],
    competences: [createCompetenceInitial()],
    modules: [] as Module[],
    teachers: [] as ProgramRequestTeacher[],
    additionalDocumentation: [] as AdditionalDocumentation[]
  };
}

export function mapProgramRequestData(programRequest: ProgramRequest, programRequestDetail: ProgramRequestDetail): ProgramRequestData {
  return {
    id: programRequest.id,
    detailId: programRequestDetail.id,
    stateId: programRequestDetail.stateId,
    institutionId: programRequest.institutionId,
    requestDate: programRequest.requestDate,
    statusId: programRequest.requestStatusId,
    programTypeId: programRequest.programTypeId ? programRequest.programTypeId : -1,
    programId: programRequestDetail.programId,
    programName: programRequestDetail.programName,
    hkoLevelId: programRequestDetail.hkoLevelId,
    guidedLearningDuration: programRequestDetail.guidedLearningDuration,
    independentLearningDuration: programRequestDetail.independentLearningDuration,
    workLearningDuration: programRequestDetail.workLearningDuration,
    qualificationTypeId: programRequestDetail.qualificationTypeId,
    educationSectorId: programRequestDetail.educationSectorId,
    educationSubSectorId: programRequestDetail.educationSubSectorId,
    languageLevelGeneralIds: programRequestDetail.languageLevelGeneralIds,
    languageLevelBusinessIds: programRequestDetail.languageLevelBusinessIds,
    remarkInstitution: programRequestDetail.remarkInstitution,
    remark: programRequestDetail.remark,
    formOfTeachingIds: programRequestDetail.formOfTeachingIds,
    knowledgeChecksIds: programRequestDetail.knowledgeChecksIds,
    enrollmentConditionsIds: programRequestDetail.enrollmentConditionsIds,
    otherEnrollmentConditions: programRequestDetail.otherEnrollmentConditions,
    otherFormsOfTeaching: programRequestDetail.otherFormsOfTeaching,
    otherKnowledgeChecks: programRequestDetail.otherKnowledgeChecks,
    executionResolutionDocuments: programRequest.programRequestResolutions,
    curriculumProgramDocument: programRequestDetail.curriculumProgramDocument,
    locationInsideHeadquarters: programRequestDetail.locationHeadquarter,
    locationOutsideHeadquarters: programRequestDetail.outsideHeadquarter,
    locationOtherLegalEntity: false,
    locationOutsideHeadquartersIds: programRequestDetail.outsideHeadquarterIds,
    otherLegalEntityLocations: [], //programRequestDetail.locationOtherLegalEntity,
    competences: programRequestDetail.competences,
    modules: programRequestDetail.modules,
    teachers: programRequestDetail.programRequestTeachers,
    additionalDocumentation: programRequestDetail.additionalDocumentation
  }
}

export function createProgramRequestCorrectionFromProgramRequest(programRequest: ProgramRequest, programRequestDetail: ProgramRequestDetail): ProgramRequestData {
  return {
    id: programRequest.id,
    detailId: 0,
    stateId: 2,
    institutionId: programRequest.institutionId,
    requestDate: programRequest.requestDate,
    statusId: programRequest.requestStatusId,
    programTypeId: programRequest.programTypeId ? programRequest.programTypeId : -1,
    programId: programRequestDetail.programId,
    programName: programRequestDetail.programName,
    hkoLevelId: programRequestDetail.hkoLevelId,
    guidedLearningDuration: programRequestDetail.guidedLearningDuration,
    independentLearningDuration: programRequestDetail.independentLearningDuration,
    workLearningDuration: programRequestDetail.workLearningDuration,
    qualificationTypeId: programRequestDetail.qualificationTypeId,
    educationSectorId: programRequestDetail.educationSectorId,
    educationSubSectorId: programRequestDetail.educationSubSectorId,
    languageLevelGeneralIds: programRequestDetail.languageLevelGeneralIds,
    languageLevelBusinessIds: programRequestDetail.languageLevelBusinessIds,
    remarkInstitution: programRequestDetail.remarkInstitution,
    remark: programRequestDetail.remark,
    formOfTeachingIds: programRequestDetail.formOfTeachingIds,
    knowledgeChecksIds: programRequestDetail.knowledgeChecksIds,
    enrollmentConditionsIds: programRequestDetail.enrollmentConditionsIds,
    otherEnrollmentConditions: programRequestDetail.otherEnrollmentConditions,
    otherFormsOfTeaching: programRequestDetail.otherFormsOfTeaching,
    otherKnowledgeChecks: programRequestDetail.otherKnowledgeChecks,
    executionResolutionDocuments: programRequest.programRequestResolutions,
    curriculumProgramDocument: programRequestDetail.curriculumProgramDocument,
    locationInsideHeadquarters: programRequestDetail.locationHeadquarter,
    locationOutsideHeadquarters: programRequestDetail.outsideHeadquarter,
    locationOtherLegalEntity: false,
    locationOutsideHeadquartersIds: programRequestDetail.outsideHeadquarterIds,
    otherLegalEntityLocations: [],
    additionalDocumentation: programRequestDetail.additionalDocumentation,

    // competences: programRequestDetail.competences,
    competences: programRequestDetail.competences.map(comp => ({ id: undefined, name: comp.name, sourceCompetenceId: comp.id })),
    // modules: programRequestDetail.modules,
    modules: programRequestDetail.modules.map(module => ({ id: 0, name: module.name, learningSets: module.learningSets.map(ls => ({ id: 0, name: ls.name, code: ls.code, level: ls.level, csvet: ls.csvet })) })),
    // teachers: programRequestDetail.programRequestTeachers
    teachers: programRequestDetail.programRequestTeachers.map(teacher => ({
      id: teacher.id, programRequestDetailId: teacher.programRequestDetailId, oib: teacher.oib, firstName: teacher.firstName,
      lastName: teacher.lastName, programRequestClassEnsambles: teacher.programRequestClassEnsambles.map(ensamble => ({ id: ensamble.id, ensamble: normalizeEnsambleValue(ensamble.ensamble) })),
      statement: teacher.statement, diplomaDocument: teacher.diplomaDocument, additionalDocumentation: teacher.additionalDocumentation,
      languages: teacher.languages?.map(language => ({
        id: language.id, language: language.language, dateFrom: language.dateFrom, dateTo: language.dateTo,
        certificationDocument: language.certificationDocument
      })), isLanguageMandatory: teacher.isLanguageMandatory, oibChecked: teacher.oibChecked
    }))
  }
}

export function createProgramRequestCopy(programRequest: ProgramRequest, programRequestDetail: ProgramRequestDetail): ProgramRequestData {
  var programRequestDetailCopy = mapProgramRequestData(programRequest, programRequestDetail);
  programRequestDetailCopy.detailId = 0;
  programRequestDetailCopy.stateId = 2;
  return programRequestDetailCopy;
}

